import { Duration } from 'luxon'
import { isEqual, orderBy } from 'lodash'

import { ComputedRef } from 'vue'
import { AnalyticsRequest } from './types'
import { AnalyticsField } from './enums'
import { FilterComparison } from '@/components/UI/Filter/utils/enums'
import type { DropdownListData, Filter, RepositoryTimeline, TagClass } from '@types'
import { TagsBunchStore } from '@/store/tags/utils/types'

import { ASSET_FIELD, TAG_FIELD, UNKNOWN_VALUE } from '@/const'
import { TABLE_NAME as ASSETS_TABLE_NAME } from '@/store/assets'
import { TABLE_NAME as TAGS_TABLE_NAME } from '@/store/tags'

import { DuckDBService } from '@/services/duckdb'
import { escapeSqlString, formatSqlIdentifier } from '@/helpers/common'

import { buildNotEqualOptionsQuery, getAnalyticsFilterOptionsConditions } from './filterOptions'

export const prepareDatesRangeSlice = (dates: string[], periods?: number) => {
  if (!dates.length || !periods) return []

  if (dates.length > periods) {
    dates = dates.slice(-periods)
  }
  return dates
}

export const convertToSeconds = (millis: number) =>
  Math.ceil(Duration.fromMillis(millis).as('seconds'))

export const compareRequestWithResponse = (
  request: AnalyticsRequest | undefined,
  repositoryId: string | undefined,
  timeline: RepositoryTimeline,
  rootId: string | undefined,
) => {
  if (!request || !repositoryId || !rootId) return false
  const compareRepositoryId = request.rid === repositoryId
  const compareTimelines = isEqual(request.timeline, timeline)
  const compareParams =
    (request.data === 'position' &&
      request.params.accounts === JSON.stringify([rootId])) ||
    request.params.root === rootId
  return compareRepositoryId && compareTimelines && compareParams
}

export const mapTags = (
  tags: string[],
  tagsBunch: Map<string, TagClass>,
  tagsList?: string[],
): Record<string, string> => {
  const result = Object.fromEntries(
    tagsList?.map(tag => [tag, UNKNOWN_VALUE]) || [],
  )
  tags.forEach(tagId => {
    const tag = tagsBunch.get(tagId)
    if (tag) {
      result[tag.field<string>(TAG_FIELD.TAG_NAME).value] = tag.field<string>(
        TAG_FIELD.TAG_VALUE,
      ).value
    }
  })
  return result
}

// build the trailing WHERE clause from a list of conditions (empty ones dropped)
const buildWhereClause = (...conditions: (string | undefined | false)[]) => {
  const active = conditions.filter((c): c is string => !!c)
  return active.length ? ` WHERE ${active.join(' AND ')}` : ''
}

export const getFilterValues = (
  tableName: string,
  date: ComputedRef<string | undefined>,
  scoped: boolean = false,
  tagsBunchStore?: TagsBunchStore,
) => {
  const duckdbInstance = DuckDBService.instance()
  return async (fieldName: string, valueField: string = ASSET_FIELD.NAME, context?: Filter, comparison?: FilterComparison) => {
    const selectedDate = date.value
    if (scoped && !selectedDate) return []
    const narrowing = getAnalyticsFilterOptionsConditions(context, tagsBunchStore?.getMetricTags, ASSETS_TABLE_NAME, TAGS_TABLE_NAME)
    const dateCondition = scoped ? `tn.${AnalyticsField.DATE} = '${escapeSqlString(selectedDate || '')}'` : ''
    const narrowCondition = narrowing?.where ? `(${narrowing.where})` : ''
    const joinsClause = narrowing?.joins.length ? ` ${narrowing.joins.join(' ')}` : ''
    const valueExpr = `ast.${valueField}`
    const baseFrom = `FROM ${tableName} as tn
      LEFT JOIN ${ASSETS_TABLE_NAME} ast ON ast.id = tn.${formatSqlIdentifier(fieldName)}`

    // For NOT EQUAL, only include options that leave some rows after exclusion
    if (comparison === FilterComparison.NOTEQ) {
      return await duckdbInstance.query<DropdownListData>(
        buildNotEqualOptionsQuery(
          valueExpr,
          `${baseFrom}${joinsClause}${buildWhereClause(dateCondition, narrowCondition)}`,
          `${baseFrom}${buildWhereClause(dateCondition)}`,
        ),
      )
    }

    const sql = `
      SELECT DISTINCT
        ${valueExpr} AS label,
        ${valueExpr} AS value
      ${baseFrom}${joinsClause}${buildWhereClause(dateCondition, narrowCondition)}
      ORDER BY label
    `
    const result = await duckdbInstance.query<DropdownListData>(sql)
    return result
  }
}

export const getFilterTags = (
  tableName: string,
  date: ComputedRef<string | undefined>,
  tagsBunchStore: TagsBunchStore,
  scoped: boolean = false,
) => {
  const duckdbInstance = DuckDBService.instance()
  return async (field: string, context?: Filter, comparison?: FilterComparison) => {
    const selectedDate = date.value
    if (scoped && !selectedDate) return []
    const narrowing = getAnalyticsFilterOptionsConditions(context, tagsBunchStore.getMetricTags, ASSETS_TABLE_NAME, TAGS_TABLE_NAME)
    const scopedDateCondition = scoped ? `tn.${AnalyticsField.DATE} = '${escapeSqlString(selectedDate || '')}'` : ''
    const narrowCondition = narrowing?.where ? `(${narrowing.where})` : ''
    const joinsClause = narrowing?.joins.length ? ` ${narrowing.joins.join(' ')}` : ''

    // For NOT EQUAL, only include options that leave some rows after exclusion
    if (comparison === FilterComparison.NOTEQ) {
      const valueExpr = `COALESCE((SELECT tg.${TAG_FIELD.TAG_VALUE} FROM UNNEST(tn.${AnalyticsField.TAGS}) AS u(tag_id) JOIN ${TAGS_TABLE_NAME} tg ON tg.${TAG_FIELD.ID} = u.tag_id WHERE tg.${TAG_FIELD.TAG_NAME} = '${escapeSqlString(field)}' LIMIT 1), '${escapeSqlString(UNKNOWN_VALUE)}')`
      const sql = buildNotEqualOptionsQuery(
        valueExpr,
        `FROM ${tableName} as tn${joinsClause}${buildWhereClause(scopedDateCondition, narrowCondition)}`,
        `FROM ${tableName} as tn${buildWhereClause(scopedDateCondition)}`,
      )
      return orderBy(await duckdbInstance.query<DropdownListData>(sql), 'label')
    }

    const dateCondition = [scopedDateCondition, narrowCondition].filter(Boolean).join(' AND ')
    const result = await duckdbInstance.query<{ tag: string }>(
      `SELECT DISTINCT(unnest(tn.${AnalyticsField.TAGS})) as tag FROM ${tableName} as tn${joinsClause}${dateCondition ? ` WHERE ${dateCondition}` : ''}`,
    )
    const tagIds: string[] = []
    const list = result?.reduce((acc, item) => {
      const tagId = item.tag
      const tag = tagsBunchStore.getElementById(tagId)
      const tagName = tag?.field<string>(TAG_FIELD.TAG_NAME).value
      if (tagName === field) {
        const tagValue = tag?.field<string>(TAG_FIELD.TAG_VALUE).value || UNKNOWN_VALUE
        tagValue && acc.push({ value: tagValue, label: tagValue })
        tagIds.push(tagId)
      }
      return acc
    }, [] as DropdownListData[]) || []
    if (tagIds.length) {
      const escapedTagIds = tagIds.map(tagId => tagId.replace(/'/g, '\'\''))
      const arrayLiteral = escapedTagIds.map(id => `'${id}'`).join(',')
      const noValueConditions = [dateCondition, `(tn.${AnalyticsField.TAGS} IS NULL OR NOT (tn.${AnalyticsField.TAGS} && ARRAY[${arrayLiteral}]))`]
        .filter(Boolean)
        .join(' AND ')
      const resultWithoutValue = await duckdbInstance.queryOne<{ count: number }>(
        `SELECT count(*) as count FROM ${tableName} as tn${joinsClause} WHERE ${noValueConditions}`,
      )
      const count = parseInt(resultWithoutValue?.count?.toString() || '0')
      if (count) {
        list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
      }
    }
    if (!list?.length) {
      list?.push({ value: UNKNOWN_VALUE, label: UNKNOWN_VALUE })
    }
    return orderBy(list, 'label')
  }
}

// SQL builders live in ./queries so they can be imported without this module's
// store/DuckDB dependencies — re-exported here to keep existing call sites unchanged.
export {
  expandDynamicField,
  getAnalyticsBaseQuerySelect,
  getAnalyticsDuckDbFieldName,
  getAnalyticsSummarySelect,
  getAnalyticsTotalQuerySelect,
  isAnalyticsDuckDbDynamicField,
  mapAnalyticsDuckDbFilter,
  prepareAnalyticsDuckDbFilterParam,
} from './queries'
